import { PartnerImageNode } from "../Partner/Partner";
import { ProductType } from "./ProductList";

export type ProductFeatureListDisplay = {
  categories: Category[]
  footerLink: string
}

export type Category = {
  name: string
  productFeatures: ProductFeature[]
}

export type ProductFeature = {
  title: string
  details: string
  image: PartnerImageNode
}

const CollabFeatureList: string[] = [
  "collaboration.accessManagement.archive",
  "collaboration.provisioning",
  "collaboration.automation",
  "collaboration.guestUsers",
  "collaboration.lifecycle",
  "collaboration.monitoringAndAdministration",
  "collaboration.endUserFocusAndOwnership",
  "collaboration.sharedFiles",
  "collaboration.policies",
  "collaboration.accessReviewsAndPermission",
  "collaboration.integratedInYourToolchain",
  "collaboration.approvalProcedures"
];

const MailFeatureList: string[] = [
  "mail.creation",
  "mail.ownershipTracking",
  "mail.accessReview",
  "mail.complianceAutomation",
  "mail.cleanUp",
  "mail.integratedInYourToolchain"
];

const MeetFeatureList: string[] = [
  "meet.createPolls",
  "meet.voteForTimeSlots",
  "meet.automaticCalendarUpdates",
  "meet.delegate",
  "meet.security",
  "meet.acrossOrganizations"
];

const IdentityFeatureList: string[] = [
  "identity.createPolls",
  "identity.voteForTimeSlots",
  "identity.automaticCalendarUpdates",
  "identity.delegate",
  "identity.security",
  "identity.acrossOrganizations"
];

export function getFeaturesByType(product: ProductType): string[] {
  switch (product) {
    case ProductType.Collab:
      return CollabFeatureList;
    case ProductType.Mail:
      return MailFeatureList;
    case ProductType.Meet:
      return MeetFeatureList;
    case ProductType.Identity:
      return IdentityFeatureList;
    default:
      throw new Error('Invalid product type');
  }
}

export function getProductNutshellDescription(productType: ProductType): string {
  switch (productType) {
    case ProductType.Collab:
      return "collab.description";
    case ProductType.Mail:
      return "mail.description";
    case ProductType.Meet:
      return "meet.description";
    case ProductType.Identity:
      return "identity.description";
    default:
      throw new Error('Invalid product type');
  }
}

type CollabKeys = "accessManagement" | "templatesAndProvisioning" | "policies" | "reportingAndIntegrations";
type MailKeys = "accessManagement" | "templatesAndProvisioning" | "policies" | "reportingAndIntegrations";
type MeetKeys = "userFriendlyAccess" | "pollAndScheduling" | "calendarAndNotifications";
type IdentityKeys = "accessManagement" |"compliance" |"automationIntegration";
// Feature Lists with Strongly Typed Keys
const CollabKeyFeatureList: Record<CollabKeys, string[]> = {
  accessManagement: [
    "collab.accessManagement.accessReviewsAndApprovals",
    "collab.accessManagement.guestAccountOnboardingAndOffboarding",
    "collab.accessManagement.archiveAndCleanup",
  ],
  templatesAndProvisioning: [
    "collab.templatesAndProvisioning.basicTemplates",
    "collab.templatesAndProvisioning.customTemplatesWithYourData",
    "collab.templatesAndProvisioning.advancedProvisioningActions",
  ],
  policies: [
    "collab.policies.lifecycleManagement",
    "collab.policies.ownerAndGuestUserPolicies",
    "collab.policies.monitoringAndAudit",
  ],
  reportingAndIntegrations: [
    "collab.reportingAndIntegrations.logToReport",
    "collab.reportingAndIntegrations.comprehensiveAdminOverview",
    "collab.reportingAndIntegrations.integratedInYourToolchain",
  ],
};

const MailKeyFeatureList: Record<MailKeys, string[]> = {
  accessManagement: [
    "mail.accessManagement.accessReviews",
    "mail.accessManagement.approvalWorkflows",
    "mail.accessManagement.automatedEscalations",
  ],
  templatesAndProvisioning: [
    "mail.templatesAndProvisioning.customizableTemplates",
    "mail.templatesAndProvisioning.flexibleProvisioning",
    "mail.templatesAndProvisioning.smartSchedulingAndResourceManagement",
  ],
  policies: [
    "mail.policies.lifecycleManagement",
    "mail.policies.ownerPolicies",
    "mail.policies.guestUserPoliciesForDistributionLists",
  ],
  reportingAndIntegrations: [
    "mail.reportingAndIntegrations.trackAndAnalyzeGovernanceActions",
    "mail.reportingAndIntegrations.comprehensiveAdminOverview",
    "mail.reportingAndIntegrations.integratedInMicrosoftTeamsAndOutlook",
  ],
};

const MeetKeyFeatureList: Record<MeetKeys, string[]> = {
  userFriendlyAccess: [
    "meet.userFriendlyAccess.easyAuthentication",
    "meet.userFriendlyAccess.magicLinksForExternalUsers",
    "meet.userFriendlyAccess.delegateFunctionality",
  ],
  pollAndScheduling: [
    "meet.pollAndScheduling.meetingPollCreation",
    "meet.pollAndScheduling.customTimeslotSelection",
    "meet.pollAndScheduling.automaticMeetingScheduling",
  ],
  calendarAndNotifications: [
    "meet.calendarAndNotifications.calendarIntegration",
    "meet.calendarAndNotifications.notificationAndSharing",
    "meet.calendarAndNotifications.microsoft365Integration",
  ],
};

const IdentityKeyFeatureList: Record<IdentityKeys, string[]> = {
  accessManagement: [
    "identity.accessManagement.appOverview",
    "identity.accessManagement.expirationManagement",
    "identity.accessManagement.accountability",
  ],
  compliance: [
    "identity.compliance.automatedCompliance",
    "identity.compliance.customNotifications",
    "identity.compliance.teamsNotifications",
  ],
  automationIntegration: [
    "identity.automationIntegration.automatedRenewal",
    "identity.automationIntegration.integratedInfrastructure",
    "identity.automationIntegration.customMetadataMamangement",
  ],
};

export function getMenuByType(product: ProductType): string[] {
  switch (product) {
    case ProductType.Collab:
      return Object.keys(CollabKeyFeatureList) as CollabKeys[];
    case ProductType.Mail:
      return Object.keys(MailKeyFeatureList) as MailKeys[];
    case ProductType.Meet:
      return Object.keys(MeetKeyFeatureList) as MeetKeys[];
    case ProductType.Identity:
      return Object.keys(IdentityKeyFeatureList) as IdentityKeys[];
    case ProductType.Tipster:
      return [];
    default:
      throw new Error("Invalid product type");
  }
}

export const getDetailsByMenuItem = (product: ProductType, menuItem: string): string[] => {
  switch (product) {
    case ProductType.Collab:
      return CollabKeyFeatureList[menuItem as CollabKeys] || [];
    case ProductType.Mail:
      return MailKeyFeatureList[menuItem as MailKeys] || [];
    case ProductType.Meet:
      return MeetKeyFeatureList[menuItem as MeetKeys] || [];
      case ProductType.Identity:
        return IdentityKeyFeatureList[menuItem as IdentityKeys] || [];
    case ProductType.Tipster:
      return [];
    default:
      throw new Error("Invalid product type or menu item");
  }
}


export const getProductClass = (product: ProductType): string => {
  switch (product) {
    case ProductType.Collab:
      return "collab";
    case ProductType.Mail:
      return "mail";
    case ProductType.Meet:
      return "meet";
    case ProductType.Identity:
      return "identity";
    case ProductType.Tipster:
      return "tipster";
    default:
      throw new Error("Invalid product type or menu item");
  }
}
